const OFFLINE_QUEUE_DB = "offline_cart_queue";
const OFFLINE_QUEUE_STORE = "items";
const OFFLINE_QUEUE_FALLBACK_KEY = "offline_cart_queue_items";
const OFFLINE_QUEUE_BASE_KEY = "offline_cart_queue_base";
const OFFLINE_QUEUE_CHANGES_KEY = "offline_cart_queue_changes";
const OFFLINE_QUEUE_BASE_ITEMS_KEY = "offline_cart_queue_base_items";

const isIdbSupported = () => "indexedDB" in window;

const openOfflineDb = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(OFFLINE_QUEUE_DB, 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(OFFLINE_QUEUE_STORE)) {
        db.createObjectStore(OFFLINE_QUEUE_STORE, { keyPath: "variantId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const formatMoney = (cents, format = "") => {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString =
    format || window.themeVariables?.settings?.moneyFormat || "{{amount}}";

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    const safeNumber = Number.isFinite(number) ? number : 0;
    const fixed = (safeNumber / 100).toFixed(precision);
    const parts = fixed.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? `${decimal}${parts[1]}` : "";
    return `${dollarsAmount}${centsAmount}`;
  };

  let value = "";
  switch (formatString.match(placeholderRegex)?.[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
    default:
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

class OfflineCartQueue {
  constructor() {
    this.items = new Map();
    this.db = null;
    this.storageMode = "local";
    this.isSyncing = false;
    this.renderTimeout = null;
    this.baseCartCount = null;
    this.baseCartTotalCents = null;
    this.suppressDispatch = false;
    this.pendingChanges = new Map();
    this.baseItems = new Map();
    this.ready = this.init();
  }

  async init() {
    await this.initStorage();
    await this.loadItems();
    this.loadBaseState();
    this.loadBaseItems();
    this.loadPendingChanges();
    this.bindEvents();
    this.dispatchChange();
    this.renderDrawer();
    if (navigator.onLine) {
      this.sync();
    }
  }

  async initStorage() {
    if (!isIdbSupported()) {
      this.storageMode = "local";
      return;
    }

    try {
      this.db = await openOfflineDb();
      this.storageMode = "idb";
    } catch (error) {
      console.warn("[OfflineQueue] IndexedDB unavailable, fallback to localStorage", error);
      this.storageMode = "local";
    }
  }

  bindEvents() {
    window.addEventListener("online", () => this.sync());
    document.addEventListener("cart:change", (event) => {
      const cart = event.detail?.cart;
      if (cart) {
        this.baseCartCount = cart.item_count;
        this.baseCartTotalCents = cart.total_price;
        this.persistBaseState();
        this.updateBaseItemsFromCart(cart);
      }
      this.scheduleRender();
    });
    document.addEventListener("cart:refresh", () => {
      this.refreshBaseCartState();
      this.scheduleRender();
    });
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        this.scheduleRender();
      }
    });
  }

  scheduleRender() {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    this.renderDrawer();
    this.renderTimeout = setTimeout(() => {
      this.renderDrawer();
    }, 1500);
  }

  async loadItems() {
    const items = this.storageMode === "idb" ? await this.idbGetAll() : this.localGetAll();
    this.items = new Map(items.map((item) => [String(item.variantId), item]));
  }

  loadBaseState() {
    try {
      const stored = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_BASE_KEY) || "{}");
      if (typeof stored.count === "number") {
        this.baseCartCount = stored.count;
      }
      if (typeof stored.totalCents === "number") {
        this.baseCartTotalCents = stored.totalCents;
      }
    } catch (error) {
      console.warn("[OfflineQueue] Failed to load base state", error);
    }
  }

  persistBaseState() {
    try {
      const payload = {
        count: this.baseCartCount,
        totalCents: this.baseCartTotalCents
      };
      localStorage.setItem(OFFLINE_QUEUE_BASE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn("[OfflineQueue] Failed to persist base state", error);
    }
  }

  loadBaseItems() {
    try {
      const stored = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_BASE_ITEMS_KEY) || "[]");
      this.baseItems = new Map(
        stored.map((item) => [String(item.key), item])
      );
    } catch (error) {
      console.warn("[OfflineQueue] Failed to load base items", error);
    }
  }

  persistBaseItems() {
    try {
      const payload = Array.from(this.baseItems.values());
      localStorage.setItem(OFFLINE_QUEUE_BASE_ITEMS_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn("[OfflineQueue] Failed to persist base items", error);
    }
  }

  updateBaseItemsFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const nextItems = cart.items.map((item) => {
      const unitPrice = parseInt(item.final_price || item.price || 0, 10);
      return {
        key: item.key,
        quantity: item.quantity,
        unitPrice
      };
    });
    this.baseItems = new Map(nextItems.map((item) => [String(item.key), item]));
    this.persistBaseItems();
  }

  loadPendingChanges() {
    try {
      const stored = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_CHANGES_KEY) || "[]");
      this.pendingChanges = new Map(
        stored.map((item) => [String(item.key), item])
      );
    } catch (error) {
      console.warn("[OfflineQueue] Failed to load pending changes", error);
    }
  }

  persistPendingChanges() {
    try {
      const payload = Array.from(this.pendingChanges.values());
      localStorage.setItem(OFFLINE_QUEUE_CHANGES_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn("[OfflineQueue] Failed to persist pending changes", error);
    }
  }

  localGetAll() {
    try {
      const stored = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_FALLBACK_KEY) || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      console.warn("[OfflineQueue] Failed reading localStorage", error);
      return [];
    }
  }

  localSaveAll() {
    const items = Array.from(this.items.values());
    localStorage.setItem(OFFLINE_QUEUE_FALLBACK_KEY, JSON.stringify(items));
  }

  idbGetAll() {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(OFFLINE_QUEUE_STORE, "readonly");
      const store = tx.objectStore(OFFLINE_QUEUE_STORE);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  idbPut(item) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(OFFLINE_QUEUE_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(OFFLINE_QUEUE_STORE).put(item);
    });
  }

  idbDelete(variantId) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(OFFLINE_QUEUE_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(OFFLINE_QUEUE_STORE).delete(String(variantId));
    });
  }

  getItems() {
    return Array.from(this.items.values()).sort((a, b) => a.addedAt - b.addedAt);
  }

  getTotalCents() {
    return this.getItems().reduce(
      (total, item) => total + (item.price || 0) * (item.quantity || 0),
      0
    );
  }

  getItemCount() {
    return this.getItems().reduce(
      (total, item) => total + (item.quantity || 0),
      0
    );
  }

  getPendingChangeAdjustments() {
    let countDelta = 0;
    let totalDelta = 0;
    this.pendingChanges.forEach((change) => {
      const base = this.baseItems.get(String(change.key));
      if (!base) return;
      const baseQty = parseInt(base.quantity || 0, 10);
      const targetQty = parseInt(change.quantity || 0, 10);
      const deltaQty = targetQty - baseQty;
      const unitPrice = parseInt(base.unitPrice || 0, 10);
      countDelta += deltaQty;
      totalDelta += deltaQty * unitPrice;
    });
    return { countDelta, totalDelta };
  }

  async saveItem(item) {
    if (this.storageMode === "idb") {
      await this.idbPut(item);
    } else {
      this.localSaveAll();
    }
  }

  async deleteItem(variantId) {
    if (this.storageMode === "idb") {
      await this.idbDelete(variantId);
    } else {
      this.localSaveAll();
    }
  }

  async addItem(payload) {
    await this.ready;
    const variantId = String(payload.variantId);
    const existing = this.items.get(variantId);
    const quantity = Math.max(parseInt(payload.quantity, 10) || 0, 0);
    if (!quantity) return;

    const nextItem = {
      variantId,
      productId: payload.productId || "",
      quantity: existing ? existing.quantity + quantity : quantity,
      price: parseInt(payload.price, 10) || 0,
      priceFormatted: payload.priceFormatted || "",
      title: payload.title || "",
      productUrl: payload.productUrl || "",
      variantTitle: payload.variantTitle || "",
      sku: payload.sku || "",
      properties: payload.properties || {},
      companyLocationId: payload.companyLocationId || null,
      source: payload.source || "watchlist",
      locale: payload.locale || "de",
      status: "pending",
      addedAt: existing?.addedAt || Date.now()
    };

    this.items.set(variantId, nextItem);
    await this.saveItem(nextItem);
    this.dispatchChange();
  }

  async queueLineChange(lineKey, targetQuantity) {
    await this.ready;
    const key = String(lineKey);
    const quantity = Math.max(parseInt(targetQuantity, 10) || 0, 0);
    const base = this.baseItems.get(key);
    const baseQty = base ? parseInt(base.quantity || 0, 10) : null;

    if (baseQty !== null && quantity === baseQty) {
      this.pendingChanges.delete(key);
      this.persistPendingChanges();
      this.dispatchChange();
      return;
    }

    this.pendingChanges.set(key, {
      key,
      quantity,
      status: "pending"
    });
    this.persistPendingChanges();
    this.dispatchChange();
  }

  async removeLineChange(lineKey) {
    await this.ready;
    const key = String(lineKey);
    this.pendingChanges.delete(key);
    this.persistPendingChanges();
    if (!this.suppressDispatch) {
      this.dispatchChange();
    }
  }

  async markLineChangeFailed(lineKey) {
    const key = String(lineKey);
    const change = this.pendingChanges.get(key);
    if (!change) return;
    change.status = "failed";
    this.pendingChanges.set(key, change);
    this.persistPendingChanges();
  }

  async retryLineChange(lineKey) {
    await this.ready;
    const key = String(lineKey);
    const change = this.pendingChanges.get(key);
    if (!change) return;

    change.status = "pending";
    this.pendingChanges.set(key, change);
    this.persistPendingChanges();
    this.dispatchChange();

    try {
      await this.syncSingleChange(change);
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      this.refreshBaseCartState();
    } catch (error) {
      await this.markLineChangeFailed(key);
      this.dispatchChange();
    }
  }

  async syncSingleChange(change) {
    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        id: change.key,
        quantity: change.quantity
      })
    });

    if (!response.ok) {
      throw new Error("Failed to sync line change");
    }

    await this.removeLineChange(change.key);
  }

  async updateQuantity(variantId, quantity) {
    await this.ready;
    const key = String(variantId);
    const item = this.items.get(key);
    if (!item) return;

    const nextQuantity = Math.max(parseInt(quantity, 10) || 0, 0);
    if (!nextQuantity) {
      await this.removeItem(key);
      return;
    }

    item.quantity = nextQuantity;
    this.items.set(key, item);
    await this.saveItem(item);
    this.dispatchChange();
  }

  async removeItem(variantId) {
    await this.ready;
    const key = String(variantId);
    this.items.delete(key);
    await this.deleteItem(key);
    if (!this.suppressDispatch) {
      this.dispatchChange();
    }
  }

  async markFailed(variantId) {
    const key = String(variantId);
    const item = this.items.get(key);
    if (!item) return;
    item.status = "failed";
    this.items.set(key, item);
    await this.saveItem(item);
  }

  async retryItem(variantId) {
    await this.ready;
    const key = String(variantId);
    const item = this.items.get(key);
    if (!item) return;

    item.status = "pending";
    this.items.set(key, item);
    await this.saveItem(item);
    this.dispatchChange();

    try {
      await this.syncSingleItem(item);
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      this.refreshBaseCartState();
    } catch (error) {
      await this.markFailed(item.variantId);
      this.dispatchChange();
    }
  }

  async syncSingleItem(item) {
    const formData = new FormData();
    formData.append("id", item.variantId);
    formData.append("quantity", item.quantity);
    Object.entries(item.properties || {}).forEach(([key, value]) => {
      formData.append(`properties[${key}]`, value);
    });

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: formData
    });

    if (!response.ok) {
      throw new Error("Failed to sync offline item");
    }

    await this.removeItem(item.variantId);
  }

  async sync() {
    await this.ready;
    if (!navigator.onLine || this.isSyncing) return;

    const items = this.getItems();
    const changes = Array.from(this.pendingChanges.values());
    if (!items.length && !changes.length) return;

    this.isSyncing = true;
    let syncedAny = false;
    let removedAny = false;
    let removedChanges = false;
    this.suppressDispatch = true;

    for (const item of items) {
      try {
        await this.syncSingleItem(item);
        removedAny = true;
        syncedAny = true;
      } catch (error) {
        console.error("[OfflineQueue] Sync failed", error);
        await this.markFailed(item.variantId);
      }
    }

    for (const change of changes) {
      try {
        await this.syncSingleChange(change);
        syncedAny = true;
        removedChanges = true;
      } catch (error) {
        console.error("[OfflineQueue] Change sync failed", error);
        await this.markLineChangeFailed(change.key);
      }
    }

    this.isSyncing = false;
    this.suppressDispatch = false;
    if (removedAny || removedChanges) {
      this.dispatchChange();
    }
    if (syncedAny) {
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      this.refreshBaseCartState();
    }
  }

  async refreshBaseCartState() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      this.baseCartCount = cart.item_count;
      this.baseCartTotalCents = cart.total_price;
      this.persistBaseState();
      this.updateBaseItemsFromCart(cart);
      this.updateCountBubbles();
    } catch (error) {
      console.warn("[OfflineQueue] Failed to refresh base cart state", error);
    }
  }

  dispatchChange() {
    window.dispatchEvent(
      new CustomEvent("offline-cart:change", {
        detail: {
          items: this.getItems(),
          totalCents: this.getTotalCents()
        }
      })
    );
    this.renderDrawer();
  }


  renderDrawer() {
    const drawers = document.querySelectorAll("cart-drawer");
    drawers.forEach((drawer) => this.renderDrawerFor(drawer));
    this.updateCountBubbles();
  }

  renderDrawerFor(drawer) {
    const items = this.getItems();
    const container = drawer.querySelector("[data-offline-items-container]");
    if (!container) return;

    this.bindDrawerListeners(drawer);

    const toSyncLabel =
      window.themeVariables?.strings?.offlineToSyncLabel || "To sync";
    const retryLabel =
      window.themeVariables?.strings?.offlineSyncRetry ||
      "Sync failed - retry";

    container.innerHTML = items
      .map((item) => {
        const lineTotal = (item.price || 0) * (item.quantity || 0);
        const lineTotalFormatted = formatMoney(
          lineTotal,
          window.themeVariables?.settings?.moneyWithCurrencyFormat
        );

        const title = item.title || item.variantTitle || "";
        const variantTitle =
          item.variantTitle && item.variantTitle !== "Default Title"
            ? item.variantTitle
            : "";

        const retryLink =
          item.status === "failed"
            ? `<button type="button" class="link text-sm" data-offline-retry>${retryLabel}</button>`
            : "";

        return `
          <line-item class="line-item" data-offline-item data-variant-id="${item.variantId}">
            <div class="line-item__info">
              <div class="v-stack gap-0.5">
                ${
                  item.productUrl
                    ? `<a href="${item.productUrl}" class="bold">
                        <span class="reversed-link hover:show">${title}</span>
                      </a>`
                    : `<p class="bold">${title}</p>`
                }
                <span class="text-sm text-subdued">${toSyncLabel}</span>
                <span class="text-sm">${lineTotalFormatted}</span>
              </div>
              ${variantTitle ? `<p class="text-sm text-subdued">${variantTitle}</p>` : ""}
              ${retryLink}
            </div>
            <div class="line-item__actions text-subdued">
              <div class="v-stack gap-2">
                <input
                  class="quantity-input"
                  type="text"
                  inputmode="numeric"
                  data-offline-quantity-input
                  value="${item.quantity}"
                  aria-label="${toSyncLabel}"
                >
                <span class="text-xs text-center">
                  <button type="button" class="link" data-offline-remove>${window.themeVariables?.strings?.remove || "Remove"}</button>
                </span>
              </div>
            </div>
          </line-item>
        `;
      })
      .join("");

    container.style.display = items.length > 0 ? "" : "none";

    const emptyState = drawer.querySelector(".empty-state");
    const offlineInner = drawer.querySelector("[data-offline-drawer-inner]");
    const offlineFooter = drawer.querySelector("[data-offline-footer]");
    const offlineEmptyClose = drawer.querySelector("[data-offline-empty-close]");
    if (emptyState && offlineInner) {
      const hasItems = items.length > 0 || this.pendingChanges.size > 0;
      offlineInner.hidden = !hasItems;
      emptyState.style.display = hasItems ? "none" : "";
      if (offlineFooter) {
        offlineFooter.hidden = !hasItems;
      }
      if (offlineEmptyClose) {
        offlineEmptyClose.style.display = hasItems ? "none" : "";
      }
    }

    const totalElement = drawer.querySelector("[data-cart-total-price]");
    if (totalElement) {
      if (!totalElement.dataset.baseCents) {
        totalElement.dataset.baseCents =
          totalElement.dataset.cartTotalCents || "0";
      }
      const baseCents =
        parseInt(totalElement.dataset.cartTotalCents, 10) ||
        parseInt(totalElement.dataset.baseCents, 10) ||
        this.baseCartTotalCents ||
        0;
      totalElement.dataset.baseCents = String(baseCents);
      const { totalDelta } = this.getPendingChangeAdjustments();
      const combinedTotal = baseCents + this.getTotalCents() + totalDelta;
      const moneyFormat =
        window.themeVariables?.settings?.moneyWithCurrencyFormat;
      totalElement.textContent = formatMoney(combinedTotal, moneyFormat);
    }

    const warning = drawer.querySelector("[data-offline-checkout-warning]");
    if (warning) {
      warning.style.display =
        items.length > 0 || this.pendingChanges.size > 0 ? "" : "none";
    }

    const checkoutButtons = drawer.querySelectorAll(
      "[data-offline-checkout-button], button[name=\"checkout\"]"
    );
    checkoutButtons.forEach((checkoutButton) => {
      const isOfflineBlocked =
        items.length > 0 || this.pendingChanges.size > 0;
      checkoutButton.disabled = isOfflineBlocked;
      checkoutButton.setAttribute(
        "aria-disabled",
        isOfflineBlocked ? "true" : "false"
      );

      const label = checkoutButton.querySelector("[data-offline-checkout-text]");
      if (label) {
        label.textContent = isOfflineBlocked
          ? checkoutButton.dataset.textOffline
          : checkoutButton.dataset.textDefault;
      }
    });

    this.renderPendingChangeBadges(drawer);
  }

  renderPendingChangeBadges(drawer) {
    const toSyncLabel =
      window.themeVariables?.strings?.offlineToSyncLabel || "To sync";
    const retryLabel =
      window.themeVariables?.strings?.offlineSyncRetry ||
      "Sync failed - retry";

    drawer
      .querySelectorAll("line-item:not([data-offline-item])")
      .forEach((lineItem) => {
        const input = lineItem.querySelector("[data-line-key]");
        const lineKey = input?.getAttribute("data-line-key");
        if (!lineKey) return;

        const change = this.pendingChanges.get(String(lineKey));
        const info = lineItem.querySelector(".line-item__info");
        if (!info) return;

        let badge = lineItem.querySelector("[data-offline-change-badge]");
        if (!change) {
          if (badge) {
            badge.remove();
          }
          return;
        }

        if (!badge) {
          badge = document.createElement("div");
          badge.dataset.offlineChangeBadge = "true";
          info.appendChild(badge);
        }

        if (change.status === "failed") {
          badge.innerHTML = `<button type="button" class="link text-sm" data-offline-change-retry>${retryLabel}</button>`;
        } else {
          badge.innerHTML = `<span class="text-sm text-subdued">${toSyncLabel}</span>`;
        }
      });
  }

  updateCountBubbles() {
    const offlineCount = this.getItemCount();
    const baseCount =
      typeof this.baseCartCount === "number" ? this.baseCartCount : null;
    const { countDelta } = this.getPendingChangeAdjustments();

    document.querySelectorAll("cart-count").forEach((countEl) => {
      const currentBase =
        typeof baseCount === "number"
          ? baseCount
          : parseInt(countEl.dataset.baseCount, 10) || 0;
      const nextBase = currentBase;
      const nextCount = nextBase + offlineCount + countDelta;
      countEl.dataset.baseCount = String(nextBase);
      countEl.innerText = String(nextCount);
      if (nextCount > 0) {
        countEl.classList.remove("opacity-0");
      } else {
        countEl.classList.add("opacity-0");
      }
    });

    document
      .querySelectorAll(".empty-state .count-bubble")
      .forEach((bubble) => {
        const currentBase =
          typeof baseCount === "number"
            ? baseCount
            : parseInt(bubble.dataset.baseCount, 10) || 0;
        const nextBase = currentBase;
        bubble.dataset.baseCount = String(nextBase);
        bubble.innerText = String(nextBase + offlineCount + countDelta);
      });
  }

  bindDrawerListeners(drawer) {
    if (drawer.dataset.offlineQueueBound === "true") return;
    drawer.dataset.offlineQueueBound = "true";

    drawer.addEventListener("change", (event) => {
      const input = event.target.closest("[data-offline-quantity-input]");
      if (!input) return;
      const item = input.closest("[data-offline-item]");
      if (!item) return;
      const quantity = parseInt(input.value, 10);
      this.updateQuantity(item.dataset.variantId, quantity);
    });

    drawer.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-offline-remove]");
      if (removeButton) {
        event.preventDefault();
        const item = removeButton.closest("[data-offline-item]");
        if (!item) return;
        this.removeItem(item.dataset.variantId);
        return;
      }

      const retryButton = event.target.closest("[data-offline-retry]");
      if (!retryButton) return;
      event.preventDefault();
      const item = retryButton.closest("[data-offline-item]");
      if (!item) return;
      this.retryItem(item.dataset.variantId);
      return;
    });

    drawer.addEventListener("click", (event) => {
      const retryButton = event.target.closest("[data-offline-change-retry]");
      if (!retryButton) return;
      event.preventDefault();
      const lineItem = retryButton.closest("line-item");
      if (!lineItem) return;
      const input = lineItem.querySelector("[data-line-key]");
      if (!input) return;
      this.retryLineChange(input.getAttribute("data-line-key"));
    });
  }
}

if (!window.offlineCartQueue) {
  window.offlineCartQueue = new OfflineCartQueue();
}

const attachLineItemQuantityInterceptor = () => {
  const LineItemQuantity = window.customElements.get("line-item-quantity");
  if (!LineItemQuantity || LineItemQuantity.prototype.__offlinePatched) {
    return;
  }

  const original = LineItemQuantity.prototype._changeLineItemQuantity;
  LineItemQuantity.prototype._changeLineItemQuantity = function (lineKey, targetQuantity) {
    if (!navigator.onLine && window.offlineCartQueue) {
      window.offlineCartQueue.queueLineChange(lineKey, targetQuantity);
      return;
    }
    return original.call(this, lineKey, targetQuantity);
  };

  LineItemQuantity.prototype.__offlinePatched = true;
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", attachLineItemQuantityInterceptor);
} else {
  attachLineItemQuantityInterceptor();
}
